import { useDevice } from "@app/shared/providers/DeviceProvider";

import { Sk, SkCard, SkRegion, SkText } from "./Skeleton";

import styles from "./PageSkeletons.module.scss";

/**
 * Loading placeholders that repeat the real layouts block for block (same columns, covers, radii and
 * spacing as the pages they stand for), so the page "fills in" instead of jumping when data arrives.
 */

export const CourseCardSkeleton: React.FC = () => (
  <SkCard padded={false} className={styles.courseCard}>
    <Sk h="auto" r={0} className={styles.cover} />
    <div className={styles.cardBody}>
      <SkText lines={2} h={16} last="70%" />
      <Sk w="55%" h={12} />
      <div className={styles.row}>
        <Sk h={6} r={3} />
        <Sk w={32} h={12} />
      </div>
      <Sk h={40} r={10} />
    </div>
  </SkCard>
);

export const CourseGridSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => (
  <div className={styles.grid}>
    {Array.from({ length: count }, (_, i) => (
      <CourseCardSkeleton key={i} />
    ))}
  </div>
);

/** «My learning»: welcome + continue card + numbers, then the course grid. */
export const HomeSkeleton: React.FC = () => {
  const { deviceSize } = useDevice();
  const mobile = deviceSize === "mobile";
  return (
    <SkRegion className={styles.page}>
      <div className={styles.headline}>
        <Sk w={mobile ? "80%" : 380} h={mobile ? 28 : 34} r={10} />
        <Sk w={mobile ? "60%" : 300} h={16} />
      </div>
      <div className={styles.welcome}>
        <SkCard padded={false} className={styles.hero}>
          <Sk h="auto" r={0} className={styles.heroCover} />
          <div className={styles.heroBody}>
            <Sk w={140} h={12} />
            <Sk w="75%" h={26} r={8} />
            <Sk w="60%" h={14} />
            <div className={styles.heroFooter}>
              <Sk h={8} r={4} />
              <Sk w={mobile ? "100%" : 150} h={44} r={10} />
            </div>
          </div>
        </SkCard>
        <div className={styles.stats}>
          {[0, 1, 2, 3].map((i) => (
            <SkCard key={i} className={styles.stat}>
              <Sk w={40} h={40} r={12} />
              <Sk w={48} h={24} r={6} />
              <Sk w="70%" h={12} />
            </SkCard>
          ))}
        </div>
      </div>
      <div className={styles.sectionHead}>
        <Sk w={120} h={22} />
        <Sk w={mobile ? "100%" : 300} h={36} r={10} />
      </div>
      <CourseGridSkeleton count={mobile ? 2 : 3} />
    </SkRegion>
  );
};

/** Course page: hero (cover + text + progress + button) and the program modules. */
export const CoursePageSkeleton: React.FC = () => (
  <SkRegion className={styles.page}>
    <SkCard padded={false} className={styles.courseHero}>
      <Sk h="auto" r={0} className={styles.courseCover} />
      <div className={styles.courseHeroBody}>
        <Sk w="80%" h={32} r={10} />
        <SkText lines={2} h={14} />
        <SkCard className={styles.progressBox}>
          <div className={styles.row}>
            <Sk w={120} h={14} />
            <Sk w={40} h={14} />
          </div>
          <Sk h={10} r={5} />
          <Sk w={160} h={12} />
        </SkCard>
        <Sk w={180} h={44} r={10} />
      </div>
    </SkCard>
    <div className={styles.program}>
      <Sk w={180} h={22} />
      {[4, 3, 0].map((rows, i) => (
        <SkCard key={i} padded={false} className={styles.module}>
          <div className={styles.moduleHead}>
            <Sk w="55%" h={18} />
            <Sk w={28} h={28} r={14} />
          </div>
          {Array.from({ length: rows }, (_, j) => (
            <div key={j} className={styles.stepRow}>
              <Sk w={26} h={26} r={13} />
              <Sk w={`${55 + ((j * 13) % 30)}%`} h={14} />
            </div>
          ))}
        </SkCard>
      ))}
    </div>
  </SkRegion>
);

/** The reading column of a lesson / article: section label, title, step parts, paragraphs. */
export const LessonContentSkeleton: React.FC<{ parts?: boolean }> = ({ parts = true }) => (
  <SkRegion className={styles.reading}>
    <Sk w={200} h={12} />
    <Sk w="70%" h={34} r={10} />
    {parts && <Sk h={52} r={999} className={styles.parts} />}
    <SkCard className={styles.paper}>
      <Sk w="50%" h={26} r={8} />
      <SkText lines={4} h={14} gap={12} last="80%" />
      <Sk h="auto" r={12} className={styles.figure} />
      <SkText lines={3} h={14} gap={12} />
    </SkCard>
  </SkRegion>
);

/** Whole lesson player frame (first load): syllabus column + top bar + reading column. */
export const LessonSkeleton: React.FC = () => {
  const { deviceSize } = useDevice();
  const desktop = deviceSize === "desktop";
  return (
    <div className={styles.lesson}>
      {desktop && (
        <aside className={styles.syllabus}>
          <Sk w={110} h={10} />
          <Sk w="80%" h={18} />
          <Sk h={6} r={3} />
          <Sk w={80} h={10} />
          <div className={styles.syllabusList}>
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className={styles.stepRow}>
                <Sk w={18} h={18} r={9} />
                <Sk w={`${50 + ((i * 17) % 40)}%`} h={12} />
              </div>
            ))}
          </div>
        </aside>
      )}
      <div className={styles.lessonMain}>
        <div className={styles.topbar}>
          <Sk w={40} h={40} r={20} />
          <div className={styles.topbarTitles}>
            <Sk w={140} h={10} />
            <Sk w={240} h={14} />
          </div>
          <Sk w={72} h={36} r={18} />
        </div>
        <LessonContentSkeleton />
      </div>
    </div>
  );
};

/** Generic section page (access, achievements…): title + a grid of cards. */
export const SectionPageSkeleton: React.FC = () => (
  <SkRegion className={styles.page}>
    <div className={styles.headline}>
      <Sk w={300} h={34} r={10} />
    </div>
    <Sk w={160} h={22} />
    <CourseGridSkeleton count={3} />
  </SkRegion>
);
